/**
 * Labstep
 *
 * @module core/DataGrid
 * @desc Wrapper around AG Grid
 */

import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { DataGridCellEditor } from 'labstep-web/core/DataGrid/CellEditor';
import { DataGridCellRenderer } from 'labstep-web/core/DataGrid/CellRenderer';
import { DataGridColumnHeader as ColumnHeader } from 'labstep-web/core/DataGrid/ColumnHeader';
import { DataGridOverlayLoading } from 'labstep-web/core/DataGrid/Overlay/Loading';
import { Loader } from 'labstep-web/core/Loader';
import { GridExportService } from 'labstep-web/grid/services/grid-export.service';
import { useToggle } from 'labstep-web/hooks/toggle';
import { configService } from 'labstep-web/services/config.service';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useUpdateContextOnColumnChange } from './hooks';
import './styles.scss';
import { useDataGridContext } from 'labstep-web/contexts/grid/hook';
import type { IDataGridProps } from './types';
import { setColumnDefsField } from './utils';

export const DataGrid: React.FC<IDataGridProps> & {
  CellRenderer: typeof DataGridCellRenderer;
  CellEditor: typeof DataGridCellEditor;
} = ({
  status,
  loading,
  defaultColDef: customDefaultColDef,
  onGridReady: customOnGridReady,
  components: customComponents,
  columnDefs: customColumnDefs,
  editable = true,
  height,
  width,
  context: customContext,
  columnState,
  hidden,
  progressComponent,
  statusBar,
  emptyState,
  rowData,
  ...rest
}) => {
  const [isLicenceKeySet, toggleIsLicenseKeySet] = useToggle();
  useEffect(() => {
    LicenseManager.setLicenseKey(configService.agGridLicenseKey);
    toggleIsLicenseKeySet();
  }, []);

  const ref = useRef<AgGridReact>();
  /** Allows accessing ref through context provider */
  const { ref: contextRef } = useDataGridContext();

  /** Add listener props to update context */
  const contextProps = useUpdateContextOnColumnChange();

  const context = useMemo(
    () => ({
      ...customContext,
      editable,
    }),
    [customContext],
  );

  // needs to be updated through the API
  // to prevent redundant rerendering
  const columnDefs = useMemo(
    () =>
      !editable && customColumnDefs
        ? setColumnDefsField(customColumnDefs, 'editable', false)
        : customColumnDefs,
    [editable],
  );

  const components: IDataGridProps['components'] = useMemo(
    (): IDataGridProps['components'] => ({
      agColumnHeader: ColumnHeader,
      agColumnGroupHeader: ColumnHeader,
      ...customComponents,
    }),
    [],
  );

  const defaultColDef: IDataGridProps['defaultColDef'] = useMemo(
    (): IDataGridProps['defaultColDef'] => ({
      resizable: true,
      minWidth: 50,
      ...customDefaultColDef,
    }),
    [],
  );

  // will be removed on rerender due to status prop change
  // the ref api is not available yet on initialising grid
  // so useEffect doesn't work
  const onGridReady = useCallback<
    NonNullable<IDataGridProps['onGridReady']>
  >(
    (params) => {
      if (status?.isFetching) {
        params.api.showLoadingOverlay();
      }
      customOnGridReady?.(params);
    },
    [status],
  );

  return (
    <>
      <div
        style={{
          ...(width && { width: '100%' }),
          height: hidden ? 0 : height,
          visibility: hidden ? 'hidden' : 'visible',
        }}
        className="ag-theme-alpine"
      >
        {(!rowData || rowData?.length === 0) && emptyState
          ? emptyState
          : isLicenceKeySet && (
              <AgGridReact
                ref={(node: AgGridReact): void => {
                  ref.current = node;
                  if (contextRef) {
                    contextRef.current = node;
                  }
                }}
                // suppressColumnVirtualisation={true}
                rowData={rowData}
                columnDefs={columnDefs}
                components={components}
                defaultColDef={defaultColDef}
                domLayout={height ? 'normal' : 'autoHeight'}
                enableFillHandle
                enableRangeSelection
                enterMovesDownAfterEdit
                onGridReady={(params) => {
                  onGridReady(params);
                  if (columnState) {
                    params.columnApi.applyColumnState({
                      state: columnState,
                    });
                  }
                }}
                overlayNoRowsTemplate="<div />"
                loadingOverlayComponent={DataGridOverlayLoading}
                rowSelection="multiple"
                undoRedoCellEditing
                context={context}
                defaultCsvExportParams={
                  GridExportService.defaultCsvExportParams
                }
                defaultExcelExportParams={
                  GridExportService.defaultExcelExportParams
                }
                statusBar={statusBar}
                {...rest}
                {...contextProps}
              />
            )}
      </div>

      {ref.current?.api &&
        (status?.isFetching || loading) &&
        (progressComponent ? (
          <div className="ag-loader-with-progress-container">
            {progressComponent}
          </div>
        ) : (
          <div className="ag-loader-container">
            <Loader size="tiny" active inline /> Loading
          </div>
        ))}
    </>
  );
};

DataGrid.CellRenderer = DataGridCellRenderer;
DataGrid.CellEditor = DataGridCellEditor;
